import { useMemo, useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Button,
  Tree,
  Typography,
  type TreeExpandedState
} from '@matillion/component-library'

import { type EditorColumn } from 'api/hooks/useGetParameterOptions/types'

import {
  ParameterOverlayButton,
  ParameterOverlayFooter,
  ParameterOverlayHeader
} from 'components/ParameterOverlay'

import { useFlags } from 'hooks/useFlags'
import { useParameterOptionsAutofill } from 'hooks/useParameterOptions/useParameterOptionsAutofill'

import { useComponentValidationProvider } from 'modules/core/ComponentValidation'

import classes from './NestedDataPickerEditor.module.scss'
import { SchemaElement } from './SchemaElement'
import { type DataStructure, type NestedDataPickerEditorProps } from './types'
import {
  autofill,
  convertFromDataStructureToRootValue,
  convertFromRootValueToDataStructure,
  flattenDataStructure,
  modifyAllElements
} from './utils'

export const NestedDataPickerEditor: FC<NestedDataPickerEditorProps> = ({
  editorColumns,
  onDone,
  elements,
  componentId,
  metadata,
  parameter,
  parameterName
}) => {
  const { enableAutofillNestedPicker } = useFlags()
  const { isUnvalidated, setValidationEnabled } =
    useComponentValidationProvider()
  const autofillEnabled = enableAutofillNestedPicker && !isUnvalidated
  const { data: autofillData, error: autofillError } =
    useParameterOptionsAutofill({
      componentMetaData: metadata,
      componentSummaryId: componentId,
      parameter,
      isEnabled: autofillEnabled,
      requestType: 'autofill-nested'
    })

  const columns =
    editorColumns.find(
      (column: EditorColumn) => column.resourceID === 'sourceOfTheColumn'
    )?.options ?? []
  const { t } = useTranslation()
  const [dataStructure, setDataStructure] = useState<DataStructure[]>(() =>
    convertFromRootValueToDataStructure(elements, columns)
  )
  const flattened = useMemo(
    () => dataStructure.map((structure) => flattenDataStructure(structure)),
    [dataStructure]
  )

  const [allSelected, noneSelected] = useMemo(() => {
    const all = flattened.every((structure) => {
      const [, ...descendants] = Object.values(structure)

      return descendants.every((d) => {
        return d.selected
      })
    })

    const none = all
      ? false
      : flattened.every((structure) => {
          const [, ...descendants] = Object.values(structure)
          return descendants.every((d) => {
            return !d.selected
          })
        })
    return [all, none]
  }, [flattened])

  const [initialExpanded] = useState<TreeExpandedState>(() => {
    return flattened.reduce<Record<string, boolean>>((flattenedRoot, node) => {
      return {
        ...flattenedRoot,
        ...Object.keys(node).reduce(
          (acc, key) => ({
            ...acc,
            [key]: true
          }),
          {}
        )
      }
    }, {})
  })

  const [autofillNoData, setAutofillNoData] = useState(false)

  return (
    <>
      <ParameterOverlayHeader title={parameterName} />
      {isUnvalidated && (
        <Alert
          aria-label={t('parameterEditor.warnings.validationWarning')}
          className={classes.Alert}
          type="warning"
          title={t('parameterEditor.warnings.validationWarning')}
          message=""
          action={{
            text: t('parameterEditor.warnings.validationAction'),
            onClick: setValidationEnabled
          }}
        />
      )}
      {autofillError && (
        <Alert
          aria-label={autofillError.detail}
          className={classes.Alert}
          type="error"
          message={autofillError.detail}
        />
      )}
      {autofillNoData && (
        <Alert
          aria-label={t(
            'parameterEditor.NESTED_DATA_PICKER_EDITOR.autofillNoDataError'
          )}
          className={classes.Alert}
          type="error"
          title={t(
            'parameterEditor.NESTED_DATA_PICKER_EDITOR.autofillNoDataError'
          )}
          message=""
        />
      )}
      <div className={classes.Box}>
        <div className={classes.Box__Title}>
          <Typography format="bcs" weight="bold">
            {t('parameterEditor.NESTED_DATA_PICKER_EDITOR.tagline')}
          </Typography>
        </div>

        <div className={classes.Box__Content}>
          <Tree initialExpanded={initialExpanded}>
            {dataStructure.map((structure) => (
              <SchemaElement
                key={structure.key}
                path={[structure.key]}
                element={structure}
                root={structure}
                onChange={(newRoot) => {
                  setDataStructure((prevState) =>
                    prevState.map((rootDataStructure) => {
                      if (rootDataStructure.key === structure.key) {
                        return newRoot
                      }
                      return rootDataStructure
                    })
                  )
                }}
              />
            ))}
          </Tree>
        </div>
        <div className={classes.Box__Actions}>
          <Button
            onClick={() => {
              setDataStructure(convertFromRootValueToDataStructure({}, columns))
            }}
            size="sm"
            text={t('common.reset')}
            alt="secondary"
          />
          <Button
            disabled={allSelected}
            onClick={() => {
              setDataStructure(
                modifyAllElements(dataStructure, { selected: true })
              )
            }}
            size="sm"
            text={t('common.selectAll')}
            alt="secondary"
          />
          <Button
            disabled={noneSelected}
            onClick={() => {
              setDataStructure(
                modifyAllElements(dataStructure, { selected: false })
              )
            }}
            size="sm"
            text={t('common.clearAll')}
            alt="secondary"
          />
          {enableAutofillNestedPicker && (
            <Button
              disabled={isUnvalidated}
              onClick={() => {
                const hasResults = autofillData?.length

                if (hasResults) {
                  setDataStructure(autofill(autofillData, dataStructure))
                }
                setAutofillNoData(!hasResults)
              }}
              size="sm"
              text={t('parameterEditor.NESTED_DATA_PICKER_EDITOR.autofill')}
              alt="secondary"
            />
          )}
        </div>
      </div>

      <ParameterOverlayFooter>
        <ParameterOverlayButton
          text={t('common.save')}
          onClick={() => {
            onDone(convertFromDataStructureToRootValue(dataStructure))
          }}
        />
      </ParameterOverlayFooter>
    </>
  )
}
